import classNames from 'classnames';
import {StandardLonghandProperties} from 'csstype';
import React, {DataHTMLAttributes, ReactNode, useEffect, useImperativeHandle, useMemo, useRef} from 'react';
import css from './style.module.scss';
import {isBrowserContext, isMobileByScreen} from 'core/helpers';

// TODO: удалить ненужные типы после удаления неиспользуемого функционала
export type TTypographyColor =
    | 'error50'
    | 'error10'
    | 'warning50'
    | 'warning10'
    | 'info50'
    | 'primary100'
    | 'primary90'
    | 'primary80'
    | 'primary70'
    | 'primary60'
    | 'primary50'
    | 'primary30'
    | 'primary30a20'
    | 'primary20'
    | 'primary10'
    | 'secondary100'
    | 'secondary80'
    | 'gray100'
    | 'gray80'
    | 'gray70'
    | 'gray65'
    | 'gray60'
    | 'gray50'
    | 'gray40'
    | 'gray30'
    | 'gray20'
    | 'black100'
    | 'black-a50'
    | 'black-a40'
    | 'black-a10'
    | 'white100'
    | 'white-a90'
    | 'white-a70'
    | 'white-a50'
    | 'white-a10'
    | 'yellow100'
    | 'yellow80'
    | 'yellow-green80'
    | 'gray-blue80'
    | 'gray-blue60'
    | 'gray-blue50'
    | 'gray-blue30'
    | 'gray-blue20'
    | 'light-gray-blue90'
    | 'light-gray-blue80'
    | 'shadow-gray-blue-a3'
    | 'green50'
    | 'violet'
    | 'special-blue'
    | 'color-special-violet'
    | 'gray-a50'
    | 'gray-a30';

export type TTypographyVariant =
    | 'ch-title-16-r'
    | 'title-16-b'
    | 'title-18-m'
    | 'title-20-m'
    | 'title-24-m'
    | 'title-24-r'
    | 'ch-title-56-reg'
    | 'ch-title-42-reg'
    | 'ch-title-32-reg'
    | 'ch-title-24-reg'
    | 'ch-title-24-b'
    | 'ch-title-16-b'
    | 'ch-title-18-b'
    | 'ch-title-20-b'
    | 'ch-title-20-reg'
    | 'label-10-b'
    | 'label-16-r'
    | 'label-16-m'
    | 'label-18-m'
    | 'label-14-r-fluid'
    | 'label-14-m-fluid'
    | 'label-14-r'
    | 'label-14-m'
    | 'label-12-r-lt'
    | 'label-12-r'
    | 'label-12-m'
    | 'price-18-b'
    | 'price-20-b'
    | 'price-24-b'
    | 'price-32-b'
    | 'button-12-m-cl'
    | 'button-14-m-cl'
    | 'article-20'
    | 'body-16-r'
    | 'body-14-reg'
    | 'body-18-r'
    | 'body-18-m'
    | 'display-42-reg'
    | 'display-42-light'
    | 'display-44-light'
    | 'ch-display-42-reg'
    | 'ch-display-42-b'
    | 'headline-32-reg'
    | 'headline-36-r'
    | 'headline-32-m'
    | 'headline-32-reg-ch'
    | 'h1'
    | 'h1-medium'
    | 'h2'
    | 'h2-medium'
    | 'h3'
    | 'h3-medium'
    | 'h4'
    | 'h4-medium'
    | 'h5'
    | 'h5-medium'
    | 'p'
    | 'p-strong'
    | 'p-u'
    | 'label'
    | 'label-m'
    | 'label-small'
    | 'badge'
    | 'badge-m'
    | 'badge-s'
    | 'button-l'
    | 'button-m'
    | 'button-s'
    | 'h1-custom'
    | 'h2-custom'
    | 'h2-custom-regular'
    | 'h3-custom'
    | 'h4-custom'
    | 'h5-custom'
    | 'price-l'
    | 'price-preview-l'
    | 'price-preview-l-reg'
    | 'price-preview-m'
    | 'price-preview-s'
    | 'old-price-l'
    | 'old-price-preview-l'
    | 'old-price-preview-m'
    | 'p-medium'
    | 'p-medium-strong'
    | 'p-small'
    | 'p-small-strong'
    | 'badge-numbers'
    | 'label-italic'
    | 'p-custom-16'
    | 'title-20-r'
    | 'label-10-r'
    | 'label-10-m-up'
    | 'snippet-title-16-r'
    | 'snippet-title-16-b'
    | 'snippet-description-16-r'
    | 'snippet-description-14-r'
    | 'snippet-description-12-r'
    | 'price-24-b-s';

export type TTypographyElement =
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'p'
    | 'span'
    | 'div'
    | 'dd'
    | 'dt'
    | 'a'
    | 'legend'
    | 'strong';

export interface ITypographyPublicProps {
    style?: Omit<StandardLonghandProperties, 'align' | 'color'>;
    align?: StandardLonghandProperties['textAlign'];
    color?: TTypographyColor;
    element?: TTypographyElement;
    variant?: TTypographyVariant;
    variants?: {DT: TTypographyVariant; TB: TTypographyVariant; MB: TTypographyVariant};
    shrinkWidthOnTextWrapOptions?: {
        onlyMobile?: boolean;
        onResize?: boolean;
    };
}

export interface IProps extends ITypographyPublicProps, Omit<DataHTMLAttributes<any>, 'color' | 'style'> {
    className?: string;
    children?: ReactNode;
}

export const Typography = React.forwardRef<HTMLElement, IProps>(function Typography(
    {
        children,
        element = 'span',
        variant = 'p',
        color = '',
        align = '',
        style = {},
        className = '',
        variants,
        shrinkWidthOnTextWrapOptions,
        ...rest
    },
    ref,
) {
    const innerRef = useRef<HTMLElement | null>(null);

    useImperativeHandle(ref, () => {
        return innerRef.current as HTMLElement;
    });

    useEffect(() => {
        if (!shrinkWidthOnTextWrapOptions) {
            return;
        }

        if (!isBrowserContext()) {
            return;
        }

        /**
         * Текстовые узлы, при переносе слов продолжают использовать размер блока такой, который необходим для вмещения всего текста без переносов строк.
         * Уменьшить, а точнее сделать размер блока сугубо по новому размеру текста с переносом можно лишь с помощь
         */
        const shrinkTextNodeWidth = () => {
            if (!innerRef.current) {
                return;
            }

            if (!isMobileByScreen() && shrinkWidthOnTextWrapOptions.onlyMobile) {
                innerRef.current.style.width = 'unset';
                return;
            }

            innerRef.current.style.width = 'unset';

            const textNode = innerRef.current.childNodes[0];
            const range = document.createRange();

            range.setStartBefore(textNode);
            range.setEndAfter(textNode);

            const clientRect = range.getBoundingClientRect();

            innerRef.current.style.width = `${clientRect.width}px`;
        };

        shrinkTextNodeWidth();

        if (shrinkWidthOnTextWrapOptions.onResize) {
            window.addEventListener('resize', shrinkTextNodeWidth);
        }

        // eslint-disable-next-line consistent-return
        return () => {
            if (shrinkWidthOnTextWrapOptions.onResize) {
                window.removeEventListener('resize', shrinkTextNodeWidth);
            }
        };
    }, [shrinkWidthOnTextWrapOptions]);

    const variantClasses = useMemo(() => {
        if (!variants) {
            return [css[variant]];
        }

        return [css[`dt-${variants.DT}`], css[`tb-${variants.TB}`], css[`mb-${variants.MB}`]];
    }, [variant, variants]);

    return React.createElement(
        element,
        {
            ...rest,
            className: classNames(css[align], css[color], css.textRendering, className, ...variantClasses),
            ref: innerRef,
            style,
        },
        children,
    );
});
